import React, {createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState} from 'react';
import {User} from "@shared/models/user/User";
import {UserService} from "@shared/services/UserService";

interface UserContextType {
    user: User | null;
    isLoading: boolean;
    error: string | null;
    refreshUser: (silent?: boolean) => Promise<void>;
    setUserData: (userData: User) => void;
    isAuthenticated: boolean;
    logout: () => void;
    // Convenience getters for backward compatibility
    groups: string[];
    idToken: string;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

const REFRESH_INTERVAL = 2 * 60 * 1000; // 2 minutes in milliseconds
const MAX_RETRY_ATTEMPTS = 3;
const FETCH_TIMEOUT = 30000; // 30 seconds

interface UserProviderProps {
    children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({children}) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const lastFetchTimeRef = useRef<number>(0);
    const userRef = useRef<User | null>(null);
    const isFetchingRef = useRef<boolean>(false);
    const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const retryCountRef = useRef<number>(0);
    const fetchAbortControllerRef = useRef<AbortController | null>(null);

    // Update refs when state changes
    useEffect(() => {
        userRef.current = user;
    }, [user]);

    const logout = useCallback(() => {
        UserService.invalidateWhoAmICache();
        setUser(null);
        setError(null);
        setIsLoading(false);
        lastFetchTimeRef.current = 0;
        retryCountRef.current = 0;
        if (fetchAbortControllerRef.current) {
            fetchAbortControllerRef.current.abort();
            fetchAbortControllerRef.current = null;
        }
        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
            refreshTimeoutRef.current = null;
        }
    }, []);

    const isAuthenticationError = useCallback((error: any): boolean => {
        if (!error) return false;
        const errorStr = typeof error === 'string' ? error : error.message || '';
        const status = error.httpsStatus || error.status;

        // Check both string and number status codes for 401/403
        const isUnauthorized = status === '401' || status === 401 || status === '403' || status === 403;
        const hasAuthErrorMessage = errorStr && (
            errorStr.toLowerCase().includes('unauthorized') ||
            errorStr.toLowerCase().includes('forbidden') ||
            errorStr.toLowerCase().includes('authentication failed')
        );

        return isUnauthorized || hasAuthErrorMessage;
    }, []);

    const fetchUser = useCallback(async (forceRefresh = false, silent = true) => {
        const now = Date.now();

        // Skip if already fetching
        if (isFetchingRef.current) {
            return;
        }

        // Skip fetch if not forced and within refresh interval
        if (!forceRefresh && userRef.current && (now - lastFetchTimeRef.current < REFRESH_INTERVAL)) {
            return;
        }

        if (forceRefresh) {
            UserService.invalidateWhoAmICache();
        }

        isFetchingRef.current = true;

        // Abort any ongoing request
        if (fetchAbortControllerRef.current) {
            fetchAbortControllerRef.current.abort();
        }
        fetchAbortControllerRef.current = new AbortController();

        // Only show loading state for non-silent refreshes
        if (!silent) {
            setIsLoading(true);
        }
        setError(null);

        try {
            // Add timeout to the request
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('Request timeout')), FETCH_TIMEOUT);
            });

            // Fetch user data (which now includes groups and idToken)
            const fetchPromise = UserService.whoAmI();
            const userData = await Promise.race([fetchPromise, timeoutPromise]) as User;

            // Validate user data
            if (!userData || typeof userData !== 'object') {
                throw new Error('Invalid user data received');
            }

            setUser(userData);
            lastFetchTimeRef.current = now;
            retryCountRef.current = 0; // Reset retry count on success
        } catch (err: any) {
            console.error('User fetch failed:', err);

            // Handle different types of errors
            if (err.name === 'AbortError') {
                console.log('User fetch was aborted');
                return;
            }

            // Check if it's an authentication error
            const isAuthError = isAuthenticationError(err);

            if (isAuthError) {
                console.warn('Authentication error detected, logging out user - no retries will be attempted');
                setUser(null);
                setError('Authentication failed. Please log in again.');
                retryCountRef.current = 0; // Reset retry count since this is a definitive auth failure
            } else {
                // For other errors, implement retry logic (but not for auth errors like 403)
                if (retryCountRef.current < MAX_RETRY_ATTEMPTS && !silent) {
                    retryCountRef.current++;
                    console.log(`Retrying user fetch (attempt ${retryCountRef.current}/${MAX_RETRY_ATTEMPTS})`);
                    setTimeout(() => {
                        fetchUser(forceRefresh, silent).catch(console.error);
                    }, 1000 * retryCountRef.current);
                    return;
                }

                const errorMessage = err?.message || 'Failed to fetch user data';
                setError(errorMessage);
                // Only set user to null for non-silent refreshes to avoid disrupting UI
                if (!silent) {
                    setUser(null);
                }
            }
        } finally {
            isFetchingRef.current = false;
            fetchAbortControllerRef.current = null;
            // Only clear loading state if we set it
            if (!silent) {
                setIsLoading(false);
            }
        }
    }, [isAuthenticationError]); // Add dependency on isAuthenticationError

    const refreshUser = useCallback(async (silent = false) => {
        await fetchUser(true, silent); // Force refresh with optional silent mode
    }, [fetchUser]);

    const setUserData = useCallback((userData: User) => {
        setUser(userData);
        setError(null);
        lastFetchTimeRef.current = Date.now();
    }, []);

    // Initial fetch - always fetch user on mount
    useEffect(() => {
        fetchUser(true, false); // Initial load should show loading state
    }, [fetchUser]);


    // Monitor for settings updates by intercepting fetch requests
    useEffect(() => {
        const originalFetch = window.fetch;

        window.fetch = async (...args) => {
            const response = await originalFetch(...args);

            // Check if this was a successful settings update request
            const url = args[0]?.toString() || '';
            if (url.includes('/api/saas/user/settings') && response.ok && args[1]?.method === 'PUT') {
                console.log('Detected successful settings update, refreshing user data');

                // Clear any existing timeout
                if (refreshTimeoutRef.current) {
                    clearTimeout(refreshTimeoutRef.current);
                }

                // Refresh user data after a short delay to ensure the update is processed
                refreshTimeoutRef.current = setTimeout(() => {
                    console.log('Executing settings-triggered user refresh');
                    fetchUser(true, true).catch(console.error); // Settings refresh should be silent
                    refreshTimeoutRef.current = null;
                }, 1000); // 1 second delay to ensure server has processed the update
            }

            return response;
        };

        return () => {
            // Restore original fetch
            window.fetch = originalFetch;
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
                refreshTimeoutRef.current = null;
            }
        };
    }, [fetchUser]);

    // Auto refresh every 2 minutes - but only if user hasn't been refreshed recently
    useEffect(() => {
        if (!user) return;

        const interval = setInterval(() => {
            // Only auto-refresh if it's been more than 2 minutes since last fetch
            const now = Date.now();
            if (now - lastFetchTimeRef.current >= REFRESH_INTERVAL) {
                fetchUser(false, true).catch(console.error); // Auto-refresh should be silent
            }
        }, REFRESH_INTERVAL);

        return () => clearInterval(interval);
    }, [user, fetchUser]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (fetchAbortControllerRef.current) {
                fetchAbortControllerRef.current.abort();
            }
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }
        };
    }, []);

    const value: UserContextType = {
        user,
        isLoading,
        error,
        refreshUser,
        setUserData,
        isAuthenticated: !!user && !error,
        logout,
        // Convenience getters for backward compatibility
        groups: user?.groups || [],
        idToken: user?.idToken || ''
    };

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};

export const useUser = (): UserContextType => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};

export const useOptionalUser = (): UserContextType | null => {
    return useContext(UserContext) || null;
};